import { Component, inject, input, linkedSignal, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_CARD } from '@progress/kendo-angular-layout';
import { AbstractNewComponent } from '@unity/abstract/abstract-new.component';
import { AgencyMiniDto, CreatedResponseDto, RolesClient, UserCreateDto, UsersClient } from '@unity/clients';
import { DropdownAgencyControlComponent } from '@unity/forms/dropdown-agency-control/dropdown-agency-control.component';
import { DropdownControlComponent } from '@unity/forms/dropdown-control/dropdown-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardNewSharedComponent } from '@unity/shared/composed/card-new-shared/card-new-shared.component';
import { emailFormControl, isValidExternalEmailValidator, nonZeroFormControl, stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityRouteUrlTrees } from '@unity/utils/types';

class UserCreationSchema implements FormGroupControlSchema<UserCreateDto> {
  public first_name = stringFormControl();
  public last_name = stringFormControl();
  public user_name = emailFormControl({ validators: [Validators.required, isValidExternalEmailValidator()] });
  public agency_id = nonZeroFormControl();
  public role_id = nonZeroFormControl();

  public constructor(agencyId?: number) {
    this.agency_id = nonZeroFormControl({ value: agencyId });
  }
}

@Component({
  selector: 'unity-new-user-page',
  standalone: true,
  imports: [
    KENDO_BUTTON,
    KENDO_LOADER,
    KENDO_CARD,
    ReactiveFormsModule,
    DropdownAgencyControlComponent,
    DropdownControlComponent,
    TextboxControlComponent,
    PageContainerComponent,
    CardNewSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './new-user-page.component.html',
  styleUrl: './new-user-page.component.scss',
})
export class NewUserPageComponent extends AbstractNewComponent<UserCreateDto, UserCreationSchema, CreatedResponseDto> {
  private readonly usersClient = inject(UsersClient);
  private readonly rolesClient = inject(RolesClient);

  public readonly $agencyId = input(undefined, { alias: 'agencyId', transform: (value: string) => parseInt(value) });

  protected override readonly $contentHeader = signal('Create New Business Partner User');
  protected override createResourceFn$ = (form: FormGroup<UserCreationSchema>) => this.usersClient.createOne(form.getRawValue());
  protected override redirectUrlFn = (response: CreatedResponseDto): UnityRouteUrlTrees => ['/user', response.id];
  protected override newControlsFn() {
    const agencyId = this.$agencyId();
    return new UserCreationSchema(agencyId);
  }

  protected readonly $selectedAgencyId = linkedSignal(() => this.$agencyId());

  private validRolesResource = rxResource({
    params: () => {
      const selectedAgencyId = this.$selectedAgencyId();
      return selectedAgencyId ? { agencyId: selectedAgencyId } : undefined;
    },
    stream: ({ params: { agencyId } }) => this.rolesClient.getAllAsDetailed(agencyId),
  });

  protected onAgencySelect(agency: AgencyMiniDto) {
    this.$selectedAgencyId.set(agency.id);
    this.$formGroup().controls.role_id.reset(undefined);
  }

  protected $inputRoles = this.validRolesResource.value;
  protected returnToDashboardAction = QuickActions.ReturnToDashboard;
}
